// R-NAV-5 — shared notifications state for the navbar bell + /inbox (spec §18.1).
//
// Binds to the real /v1/notifications inbox API (the read side of R-Notif).
// Fetches the current user's notifications on mount (the Nav is mounted for
// the whole authed session, so this isn't per-navigation), exposes the unread
// count for the bell badge, and mark-read / mark-all-read that re-fetch so
// every surface stays in sync. Anonymous → empty + no fetch.
//
// S3 — kept fresh live: subscribes to the shared "/notifications" socket
// (see lib/notifications-socket). A server nudge coalesces (400ms) into ONE
// refetch; every (re)connect refetches immediately (backfill after downtime);
// and a slow 30s poll runs ONLY while the socket is down, so a socket-less
// session still keeps the bell fresh without doubling load when push works.
import React from "react";

import { useAuth } from "../auth/AuthContext";
import { notificationsApi } from "../api/endpoints-core.js";
import { subscribeNotificationsSocket } from "../lib/notifications-socket";

export interface NotifItem {
  id: string;
  /** Canonical template id, e.g. "application.accepted". */
  type: string;
  title: string;
  body: string;
  kind: string;
  read: boolean;
  createdAt: string;
}

interface NotifListResponse {
  items: NotifItem[];
  total: number;
  unreadCount: number;
  page: number;
  limit: number;
}

export interface UseNotifications {
  items: NotifItem[];
  unreadCount: number;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

// S3 — a burst of socket nudges becomes one refetch; polling only while the
// socket is down (reconnect backfill covers anything missed in between).
const NUDGE_COALESCE_MS = 400;
const FALLBACK_POLL_MS = 30_000;

export const useNotifications = (opts: { limit?: number } = {}): UseNotifications => {
  const { isAuthenticated } = useAuth();
  const limit = opts.limit ?? 10;
  const [items, setItems] = React.useState<NotifItem[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [socketConnected, setSocketConnected] = React.useState(false);
  const nudgeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = (await notificationsApi.list({ limit })) as NotifListResponse;
      setItems(Array.isArray(res?.items) ? res.items : []);
      setUnreadCount(typeof res?.unreadCount === "number" ? res.unreadCount : 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, limit]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  // S3 — live push: subscribe to the shared notifications socket. A nudge
  // coalesces into one refetch; connect refetches immediately (backfill).
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const unsubscribe = subscribeNotificationsSocket({
      onNudge: () => {
        if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
        nudgeTimer.current = setTimeout(() => void refresh(), NUDGE_COALESCE_MS);
      },
      onConnect: () => {
        setSocketConnected(true);
        void refresh();
      },
      onDisconnect: () => setSocketConnected(false),
    });
    return () => {
      unsubscribe();
      if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
      setSocketConnected(false);
    };
  }, [isAuthenticated, refresh]);

  // S3 — polling fallback ONLY while the socket is down.
  React.useEffect(() => {
    if (!isAuthenticated || socketConnected) return;
    const t = setInterval(() => void refresh(), FALLBACK_POLL_MS);
    return () => clearInterval(t);
  }, [isAuthenticated, socketConnected, refresh]);

  const markRead = React.useCallback(
    async (id: string) => {
      try {
        await notificationsApi.markRead(id);
      } catch {
        // best-effort; refresh reconciles
      }
      await refresh();
    },
    [refresh],
  );

  const markAllRead = React.useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
    } catch {
      // best-effort
    }
    await refresh();
  }, [refresh]);

  return { items, unreadCount, loading, error, refresh, markRead, markAllRead };
};

/**
 * Map a notification template → an in-app route to open on click (spec §18.1
 * "each notification links to its destination"). null = no navigation (the
 * click just marks it read).
 */
export const notifLink = (type: string): string | null => {
  if (type.startsWith("grade")) return "transcript";
  if (type === "user.password.claim") return "claim";
  if (type.startsWith("application")) return "track";
  // R-CHAT M4 — an in-app chat notification opens the messenger.
  if (type === "chat.message") return "messages";
  // R-CHAT M9 — a mention ping opens the messenger (same destination as a message).
  if (type === "chat.mention") return "messages";
  // S4 — «پاسخ شما برگزیده شد» opens the messenger (the Q&A lives there).
  if (type === "chat.answer.accepted") return "messages";
  // R-CONNECT — connect.request / connect.accepted → the network hub (the
  // requests tab is the destination for an incoming request; accepted shows
  // the new connection there too).
  if (type.startsWith("connect.")) return "network";
  return null;
};
